import { SelectItem } from '@upsales/components/Utils/selectHelpers';
import logError from 'Helpers/logError';
import FieldTranslations from 'Resources/FieldTranslations';
import RequestBuilder from 'Resources/RequestBuilder';
import ComparisonTypes from 'Resources/ComparisonTypes';
import type Contact from 'App/resources/Model/Contact';
import type CustomField from 'App/resources/Model/CustomField';
import type { ModalProps } from '../Modals/Modals';
import { MappedCustomFields, mapCustomValuesToObject } from '../FormObserver/FormObserver';

export const MAX_INLINE_DUPLICATES = 5;

export type FieldTranslationItem = SelectItem & { tagId: number; value: string; language?: string };
export type ContactOptin = NonNullable<Contact['optins']>[number];

export type EditContactProps = ModalProps<Contact | undefined> & {
	id?: number;
	contact?: Partial<Contact>;
	clientId?: number;
	account?: { id: number; name: string; journeyStep?: string };
	forceRequiredEmail?: boolean;
	titleCategory?: FieldTranslationItem;
	/** Accepted for caller parity (Angular fallback uses it); the React modal reads it from AppService. */
	customerId?: number;
};

/** Editable shape of a contact in the form (standalone — not the lean Contact read model). */
export type MappedContact = {
	id?: number;
	firstName: string;
	lastName: string;
	title: string;
	email: string;
	phone: string | null;
	phoneCountryCode?: string;
	cellPhone: string | null;
	cellPhoneCountryCode?: string;
	notes: string;
	client: { id: number; name: string; journeyStep?: string } | null;
	journeyStep: string;
	categories: Contact['categories'];
	projects: { id: number; name: string; title: string }[];
	salutation?: FieldTranslationItem | null;
	titleCategory?: FieldTranslationItem | null;
	optins: ContactOptin[];
	active: 1 | 0;
	isPriority: 1 | 0;
	mailBounces: Contact['mailBounces'];
	custom: MappedCustomFields;
	userEditable?: boolean;
	userRemovable?: boolean;
};

export const addSalutationFilters = (rb: RequestBuilder) => {
	rb.fields = ['id', 'name'];
	return rb.addFilter({ field: 'type' }, ComparisonTypes.Equals, 'salutation');
};

export const addTitleCategoryFilters = (rb: RequestBuilder) => {
	rb.fields = ['tagId', 'value'];
	return rb.addFilter({ field: 'type' }, ComparisonTypes.Equals, 'titlecategory');
};

export const getFieldTranslationItem = (tagId: number, modifyRb: (rb: RequestBuilder) => RequestBuilder) =>
	FieldTranslations.find(modifyRb(new RequestBuilder().addFilter({ field: 'tagId' }, ComparisonTypes.Equals, tagId)).build())
		.then((res: { data: FieldTranslationItem[] }) =>
			res.data.length ? ({ ...res.data[0], title: res.data[0].value } as FieldTranslationItem) : null
		)
		.catch((e: Error) => {
			logError(e, 'Failed to get field translation');
			return null;
		});

export const buildInitialValues = (contact: Partial<Contact>, customFields: CustomField[]): MappedContact => ({
	id: contact.id,
	firstName: contact.firstName ?? '',
	lastName: contact.lastName ?? '',
	title: contact.title ?? '',
	email: contact.email ?? '',
	phone: contact.phone ?? '',
	cellPhone: contact.cellPhone ?? '',
	notes: (contact as { notes?: string }).notes ?? '',
	client: contact.client ? { id: contact.client.id, name: contact.client.name } : null,
	journeyStep: contact.journeyStep ?? '',
	categories: contact.categories ?? [],
	projects: ((contact as { projects?: { id: number; name: string }[] }).projects ?? []).map(p => ({
		...p,
		title: p.name
	})),
	salutation: contact.salutation
		? ({ ...contact.salutation, title: contact.salutation.value } as FieldTranslationItem)
		: null,
	titleCategory: (contact as { titleCategory?: FieldTranslationItem }).titleCategory ?? null,
	optins: contact.optins ?? [],
	active: (contact.active ?? 1) as 1 | 0,
	isPriority: (contact.isPriority ? 1 : 0) as 1 | 0,
	mailBounces: contact.mailBounces ?? [],
	custom: mapCustomValuesToObject(contact.custom ?? [], customFields)
});
